import dayjs from 'dayjs';

export const splitArrayToState = entries => {
  if (!Array.isArray(entries)) return {};
  const result = {};
  for (const entry of entries) {
    const bid = entry && entry.bookingId ? String(entry.bookingId) : null;
    const tn = Number(entry && entry.teamNumber);
    if (!bid || Number.isNaN(tn)) continue;
    const count = Math.max(0, Number(entry && entry.count) || 0);
    if (!result[bid]) result[bid] = {};
    result[bid][tn] = count;
  }
  return result;
};

export const splitStateToArray = state => {
  if (!state || typeof state !== 'object') return [];
  const result = [];
  for (const [bid, teams] of Object.entries(state)) {
    if (!teams || typeof teams !== 'object') continue;
    for (const [tnRaw, countRaw] of Object.entries(teams)) {
      const tn = Number(tnRaw);
      if (Number.isNaN(tn)) continue;
      const count = Math.max(0, Number(countRaw) || 0);
      result.push({ bookingId: bid, teamNumber: tn, count });
    }
  }
  return result;
};

export const sanitizeSplitStateForAssignments = (state, assignments) => {
  if (!state || typeof state !== 'object' || !Array.isArray(assignments)) return {};
  const validPairs = new Set(
    assignments
      .map(a => {
        const bid = a && a.bookingId ? String(a.bookingId) : null;
        const tn = Number(a && a.teamNumber);
        if (!bid || Number.isNaN(tn)) return null;
        return `${bid}::${tn}`;
      })
      .filter(Boolean)
  );
  const sanitized = {};
  for (const [bid, teams] of Object.entries(state)) {
    if (!teams || typeof teams !== 'object') continue;
    for (const [tnRaw, countRaw] of Object.entries(teams)) {
      const tn = Number(tnRaw);
      if (Number.isNaN(tn)) continue;
      const key = `${String(bid)}::${tn}`;
      if (!validPairs.has(key)) continue;
      if (!sanitized[bid]) sanitized[bid] = {};
      sanitized[bid][tn] = Math.max(0, Number(countRaw) || 0);
    }
  }
  return sanitized;
};

export const areSplitStatesEqual = (a, b) => {
  const left = a || {};
  const right = b || {};
  const keysA = Object.keys(left);
  const keysB = Object.keys(right);
  if (keysA.length !== keysB.length) return false;
  for (const bid of keysA) {
    if (!Object.prototype.hasOwnProperty.call(right, bid)) return false;
    const teamsA = left[bid] || {};
    const teamsB = right[bid] || {};
    const teamKeysA = Object.keys(teamsA);
    const teamKeysB = Object.keys(teamsB);
    if (teamKeysA.length !== teamKeysB.length) return false;
    for (const tn of teamKeysA) {
      if (Number(teamsA[tn]) !== Number(teamsB[tn])) return false;
    }
  }
  return true;
};

export const normalizeSlotId = slot => {
  if (slot === 0 || slot === 1 || slot === 2) return slot;
  if (typeof slot === 'string') {
    const lower = slot.toLowerCase();
    if (lower === 'orange') return 0;
    if (lower === 'green') return 1;
    if (lower === 'all') return 2;
  }
  return null;
};

export const getSlotColorById = (slotId, theme) => {
  if (slotId === 1) return theme.palette.success.main;
  if (slotId === 0) return theme.palette.warning.main;
  if (slotId === 2 || slotId == null) return theme.palette.primary.main;
  return theme.palette.text.primary;
};

export const getSlotLabelById = slotId => {
  if (slotId === 1) return 'Grüner Slot';
  if (slotId === 0) return 'Oranger Slot';
  if (slotId === 2 || slotId == null) return 'Alle Slots';
  return 'Unbekannter Slot';
};

// Fixed Lasertag team colors by team index (1–7): vest / operator / scoreboard alignment
export const LASERTAG_TEAM_COLORS = [
  { number: 1, color: '#f44336', name: 'Rot', borderColor: '#f44336' },
  { number: 2, color: '#2196f3', name: 'Blau', borderColor: '#2196f3' },
  { number: 3, color: '#4caf50', name: 'Grün', borderColor: '#4caf50' },
  { number: 4, color: '#9c27b0', name: 'Lila', borderColor: '#9c27b0' },
  { number: 5, color: '#ffeb3b', name: 'Gelb', borderColor: '#fbc02d' },
  { number: 6, color: '#ffffff', name: 'Weiß', borderColor: '#bdbdbd' },
  { number: 7, color: '#4fc3f7', name: 'Hellblau', borderColor: '#039be5' },
];

export const isPlaceholderMember = pm => {
  try {
    if (!pm) return true;
    if (typeof pm === 'string') return false;
    return !(pm.firstName || pm.playerName);
  } catch (_) {
    return false;
  }
};

export const getPlaceholdersCount = booking => {
  try {
    const arr = Array.isArray(booking?.ltSpecific?.participatingMembers)
      ? booking.ltSpecific.participatingMembers
      : [];
    return arr.filter(isPlaceholderMember).length;
  } catch (_) {
    return 0;
  }
};

export const getEffectiveParticipantCount = booking => {
  try {
    const booked = Number(booking?.participantCount) || 0;
    const placeholders = getPlaceholdersCount(booking);
    return Math.max(0, booked - placeholders);
  } catch (_) {
    return Number(booking?.participantCount) || 0;
  }
};

export const getAdjustedEffectiveParticipantCount = (booking, sitOutCounts) => {
  try {
    const base = getEffectiveParticipantCount(booking);
    const bid = String(booking?._id || booking?.id || '');
    const sit = Math.max(0, Number(sitOutCounts?.[bid] || 0));
    return Math.max(0, base - sit);
  } catch (_) {
    return getEffectiveParticipantCount(booking);
  }
};

export const getAdjustedCountWithMap = (booking, map) => {
  try {
    const base = getEffectiveParticipantCount(booking);
    const bid = String(booking?._id || booking?.id || '');
    const sit = Math.max(0, Number((map && map[bid]) || 0));
    return Math.max(0, base - sit);
  } catch (_) {
    return getEffectiveParticipantCount(booking);
  }
};

const CHECKIN_CUTOFF_MINUTES_BEFORE_END = 10;

export const getTimingInfoForBooking = booking => {
  try {
    if (!booking) return null;
    if (booking.bookingType && booking.bookingType === 'RegiondoBooking') return null;
    const start = booking.startTime ? dayjs(booking.startTime) : null;
    const end = booking.endTime ? dayjs(booking.endTime) : null;
    if (!start || !start.isValid()) return null;
    const now = dayjs();
    if (now.isBefore(start)) {
      const mins = Math.max(0, Math.ceil(start.diff(now, 'minute', true)));
      return `Buchung startet in ${mins} Minuten`;
    }
    if (end && end.isValid() && now.isAfter(end)) {
      const mins = Math.max(0, Math.floor(now.diff(end, 'minute', true)));
      return `Buchung seit ${mins} Minuten beendet`;
    }
    if (end && end.isValid() && now.isBefore(end)) {
      const minsUntilEnd = Math.ceil(end.diff(now, 'minute', true));
      if (minsUntilEnd > 0 && minsUntilEnd <= CHECKIN_CUTOFF_MINUTES_BEFORE_END) {
        return `Buchung endet in ${minsUntilEnd} Minuten (Spiel passt nicht mehr)`;
      }
    }
    return null;
  } catch (_) {
    return null;
  }
};

// Returns structured timing info for card display with before/after highlighting
export const getTimingInfoForBookingDetailed = booking => {
  try {
    if (!booking) return null;
    if (booking.bookingType && booking.bookingType === 'RegiondoBooking') return null;
    const start = booking.startTime ? dayjs(booking.startTime) : null;
    const end = booking.endTime ? dayjs(booking.endTime) : null;
    if (!start || !start.isValid()) return null;
    const now = dayjs();
    if (now.isBefore(start)) {
      const mins = Math.max(0, Math.ceil(start.diff(now, 'minute', true)));
      return { label: `-${mins} min`, minutesDiff: mins, isBefore: true, isAfter: false };
    }
    if (end && end.isValid() && now.isAfter(end)) {
      const mins = Math.max(0, Math.floor(now.diff(end, 'minute', true)));
      return { label: `+${mins} min`, minutesDiff: mins, isBefore: false, isAfter: true };
    }
    const endLabel = end && end.isValid() ? end.format('HH:mm') : null;
    return {
      label: endLabel ? `Jetzt (bis ${endLabel})` : 'Jetzt',
      minutesDiff: 0,
      isBefore: false,
      isAfter: false,
    };
  } catch (_) {
    return null;
  }
};

// Expected number of games for a booking (duration / 30, 30 min per game)
export const getExpectedGamesForBooking = booking => {
  try {
    if (!booking?.startTime || !booking?.endTime) return null;
    const start = dayjs(booking.startTime);
    const end = dayjs(booking.endTime);
    if (!start.isValid() || !end.isValid()) return null;
    const durationMinutes = Math.max(0, end.diff(start, 'minute', true));
    return Math.floor(durationMinutes / 30);
  } catch (_) {
    return null;
  }
};

// Whether booking has played enough games (>= expected from duration/4)
export const hasPlayedEnoughGames = booking => {
  try {
    const expected = getExpectedGamesForBooking(booking);
    if (expected == null) return false;
    const played = Number(booking?.ltSpecific?.gamesPlayedCount || 0);
    return played >= expected;
  } catch (_) {
    return false;
  }
};

export const isInsideBookingWindow = booking => {
  try {
    if (!booking) return false;
    if (booking.bookingType && booking.bookingType === 'RegiondoBooking') return false;
    const start = booking.startTime ? dayjs(booking.startTime) : null;
    const end = booking.endTime ? dayjs(booking.endTime) : null;
    if (!start || !end || !start.isValid() || !end.isValid()) return false;
    const now = dayjs();
    const cutoffEnd = end.subtract(CHECKIN_CUTOFF_MINUTES_BEFORE_END, 'minute');
    return (
      (now.isAfter(start) || now.isSame(start)) &&
      (now.isBefore(cutoffEnd) || now.isSame(cutoffEnd))
    );
  } catch (_) {
    return false;
  }
};

export const computeAutoAssignments = (
  bookings,
  k,
  allowSplit = true,
  getAdjustedEffectiveParticipantCountFn
) => {
  const items = bookings
    .map(b => {
      const bid = String(b._id);
      const total = getAdjustedEffectiveParticipantCountFn(b);
      return { id: bid, size: total };
    })
    .filter(it => it.id && it.size > 0);

  if (!Array.isArray(items) || items.length === 0 || !k || k < 1) {
    return { assignments: [], splitQuantitiesMap: {} };
  }

  const sorted = [...items].sort((a, b) => b.size - a.size);
  const loads = Array.from({ length: k }, () => 0);
  const teamOf = new Map();

  for (const it of sorted) {
    let minIdx = 0;
    let minVal = loads[0];
    for (let i = 1; i < k; i++) {
      if (loads[i] < minVal) {
        minVal = loads[i];
        minIdx = i;
      }
    }
    loads[minIdx] += it.size;
    teamOf.set(it.id, minIdx + 1);
  }

  const baseline = {
    assignments: items.map(it => ({
      bookingId: it.id,
      teamNumber: teamOf.get(it.id),
      teamCount: k,
    })),
    splitQuantitiesMap: {},
  };

  if (!allowSplit) {
    const total = items.reduce((s, it) => s + it.size, 0);
    const mean = total / k;
    const score = arr => {
      const max = Math.max(...arr);
      const min = Math.min(...arr);
      const imbalance = max - min;
      const deviation = arr.reduce((s, v) => s + Math.abs(v - mean), 0);
      return { imbalance, deviation };
    };

    let currentLoads = [...loads];
    let currentTeamOf = new Map(teamOf);
    let improved = true;
    let iterations = 0;
    const maxIterations = Math.min(200, items.length * items.length);

    while (improved && iterations < maxIterations) {
      iterations += 1;
      improved = false;
      const baseScore = score(currentLoads);
      let bestSwap = null;

      for (let i = 0; i < items.length; i++) {
        for (let j = i + 1; j < items.length; j++) {
          const a = items[i];
          const b = items[j];
          const teamA = (currentTeamOf.get(a.id) || 1) - 1;
          const teamB = (currentTeamOf.get(b.id) || 1) - 1;
          if (teamA === teamB) continue;

          const nextLoads = [...currentLoads];
          nextLoads[teamA] = nextLoads[teamA] - a.size + b.size;
          nextLoads[teamB] = nextLoads[teamB] - b.size + a.size;

          const nextScore = score(nextLoads);
          const isBetter =
            nextScore.imbalance < baseScore.imbalance ||
            (nextScore.imbalance === baseScore.imbalance &&
              nextScore.deviation < baseScore.deviation);

          if (isBetter) {
            if (
              !bestSwap ||
              nextScore.imbalance < bestSwap.score.imbalance ||
              (nextScore.imbalance === bestSwap.score.imbalance &&
                nextScore.deviation < bestSwap.score.deviation)
            ) {
              bestSwap = {
                aId: a.id,
                bId: b.id,
                teamA,
                teamB,
                nextLoads,
                score: nextScore,
              };
            }
          }
        }
      }

      if (bestSwap) {
        currentLoads = bestSwap.nextLoads;
        currentTeamOf.set(bestSwap.aId, bestSwap.teamB + 1);
        currentTeamOf.set(bestSwap.bId, bestSwap.teamA + 1);
        improved = true;
      }
    }

    return {
      assignments: items.map(it => ({
        bookingId: it.id,
        teamNumber: currentTeamOf.get(it.id),
        teamCount: k,
      })),
      splitQuantitiesMap: {},
    };
  }

  const currentDiff = Math.max(...loads) - Math.min(...loads);
  if (currentDiff === 0) {
    return baseline;
  }

  let best = {
    diff: currentDiff,
    bookingId: null,
    keepTeamIdx: null,
    moveTeamIdx: null,
    moveCount: null,
  };

  for (const it of items) {
    if (it.size <= 1) continue;
    const t0 = (teamOf.get(it.id) || 1) - 1;
    for (let t1 = 0; t1 < k; t1++) {
      if (t1 === t0) continue;
      const ideal = Math.round((loads[t0] - loads[t1]) / 2);
      const candidates = new Set();
      const clamp = x => Math.max(1, Math.min(it.size - 1, x));
      candidates.add(clamp(ideal));
      candidates.add(clamp(ideal - 1));
      candidates.add(clamp(ideal + 1));
      for (const b of candidates) {
        const newLoads = [...loads];
        newLoads[t0] = loads[t0] - b;
        newLoads[t1] = loads[t1] + b;
        const diff = Math.max(...newLoads) - Math.min(...newLoads);
        if (diff < best.diff) {
          best = { diff, bookingId: it.id, keepTeamIdx: t0, moveTeamIdx: t1, moveCount: b };
          if (diff === 0) break;
        }
      }
      if (best.diff === 0) break;
    }
    if (best.diff === 0) break;
  }

  if (!best.bookingId || best.diff >= currentDiff) {
    return baseline;
  }

  const splitQuantitiesMap = {};
  const assignments = [];
  for (const it of items) {
    if (it.id === best.bookingId) {
      const a = it.size - best.moveCount;
      const b = best.moveCount;
      assignments.push({ bookingId: it.id, teamNumber: best.keepTeamIdx + 1, teamCount: k });
      assignments.push({ bookingId: it.id, teamNumber: best.moveTeamIdx + 1, teamCount: k });
      splitQuantitiesMap[String(it.id)] = {
        [best.keepTeamIdx + 1]: a,
        [best.moveTeamIdx + 1]: b,
      };
    } else {
      assignments.push({ bookingId: it.id, teamNumber: teamOf.get(it.id), teamCount: k });
    }
  }

  return { assignments, splitQuantitiesMap };
};

export const compactTeamAssignments = (assignments, currentTeamCount, splitQuantitiesMap = {}) => {
  if (!assignments || assignments.length === 0) {
    return { assignments: [], teamCount: 2, splitQuantitiesMap: {} };
  }

  const usedTeams = new Set();
  for (const assignment of assignments) {
    usedTeams.add(Number(assignment.teamNumber));
  }

  const usedTeamNumbers = Array.from(usedTeams).sort((a, b) => a - b);
  const newTeamCount = Math.max(2, usedTeamNumbers.length);

  if (usedTeamNumbers.length === 0) {
    return { assignments: [], teamCount: 2, splitQuantitiesMap: {} };
  }

  if (usedTeamNumbers.length === currentTeamCount || usedTeamNumbers.length === 1) {
    return { assignments, teamCount: currentTeamCount, splitQuantitiesMap };
  }

  const teamMapping = new Map();
  for (let i = 0; i < usedTeamNumbers.length; i++) {
    teamMapping.set(usedTeamNumbers[i], i + 1);
  }

  const compactedAssignments = assignments.map(assignment => ({
    ...assignment,
    teamNumber: teamMapping.get(Number(assignment.teamNumber)),
    teamCount: newTeamCount,
  }));

  const compactedSplitQuantities = {};
  for (const [bookingId, teamQuantities] of Object.entries(splitQuantitiesMap)) {
    const newTeamQuantities = {};
    for (const [teamNumber, quantity] of Object.entries(teamQuantities)) {
      const oldTeamNumber = Number(teamNumber);
      const newTeamNumber = teamMapping.get(oldTeamNumber);
      if (newTeamNumber !== undefined) {
        newTeamQuantities[newTeamNumber] = quantity;
      }
    }
    if (Object.keys(newTeamQuantities).length > 0) {
      compactedSplitQuantities[bookingId] = newTeamQuantities;
    }
  }

  return {
    assignments: compactedAssignments,
    teamCount: newTeamCount,
    splitQuantitiesMap: compactedSplitQuantities,
  };
};
